import React, {useCallback, useEffect, useRef, useState} from "react";
import {routes} from "../../../core/models/static/routes/app-routes";
import CacheService from "../../../core/services/cache/cache_service";
import useIsMounted from "../../hooks/use-is-mounted";
import {Redirect, Route, Switch, useHistory} from "react-router-dom";
import {useConfigureNotebook} from "../../hooks/use-notebook-config";
import {AuthenticationLoginView} from "./login";
import {AuthenticationRedirectView} from "./redirect";
import {AuthenticationLogoutView} from "./logout";
import useQuery from "../../hooks/use-query";

export const AuthenticationViews = () => {
    const [urls, setUrls] = useState(CacheService.getUrlConfig())
    const {toggleShow: toggleShowNotebooksAction} = useConfigureNotebook('auth-routes');
    const isMounted = useIsMounted();

    /**
     * As soon as the component is mounted, it hides the notebooks feature
     */
    useEffect(() => {
        toggleShowNotebooksAction(false);
    }, [toggleShowNotebooksAction])

    /**
     * Fetches the login and logout urls from the cache or the server if not cached, and updates the state with the fetched urls
     * @type {(function(): Promise<void>)|*}
     */
    const fetchUrls = useCallback(async () => {
        const urlConfig = await CacheService.getOrFetchUrlConfig();
        if (!isMounted())
            return;
        setUrls(urlConfig);
    }, [isMounted]);

    /**
     * Fetches the login and logout urls on the component mount
     */
    useEffect(() => {
        void fetchUrls();
    }, [fetchUrls]);

    const [query] = useQuery();
    const history = useHistory();
    const historyRef = useRef(history);
    historyRef.current = history;
    const returnTo = query.returnTo ?? "";

    /**
     * Navigates the user to the cached route if there was any, otherwise redirects to the landing page
     */
    const navigateToCachedRoute = useCallback(() => {
        if (returnTo) {
            const decodedReturnTo = decodeURIComponent(returnTo);
            if (decodedReturnTo) {
                historyRef.current.replace(decodedReturnTo);
                return;
            }
        }
        historyRef.current.replace(routes.landing);
    }, [returnTo])

    /**
     * Navigates the user to the access denied page
     * @type {(function(): void)}
     */
    const navigateToAccessDenied = useCallback(() => {
        historyRef.current.replace({pathname: routes.error.accessDenied, search: window.location.search});
    }, [])


    /**
     * Navigates the user to the core url for login or logout, depending on the mode provided
     * @type {(function('login' | 'logout'): void)}
     */
    const navigateToCoreUrl = useCallback((mode) => {
        const searchParams = new URLSearchParams(window.location.search);
        const returnTo = searchParams.get('returnTo');
        let ourUrl = window.location.origin + routes.auth.login;
        if (returnTo) {
            ourUrl += "?returnTo=" + encodeURIComponent(returnTo);
        }
        const base = mode === 'login' ? urls?.loginUrl : urls?.logoutUrl;
        if (base) {
            window.location.replace(`${base}?redirectUrl=${encodeURIComponent(ourUrl)}&useQueryForParams=true`);
        }
    }, [urls?.loginUrl, urls?.logoutUrl])

    const proceedToLogout = React.useMemo(() => !!urls?.logoutUrl, [urls?.logoutUrl]);

    return (
        <>
            <Switch>
                <Route path={routes.auth.login} exact>
                    <AuthenticationLoginView
                        navigateToCachedRoute={navigateToCachedRoute}
                        navigateToAccessDenied={navigateToAccessDenied}
                    />
                </Route>
                <Route path={routes.auth.logout} exact>
                    <AuthenticationLogoutView
                        proceed={proceedToLogout}
                        navigateToCoreUrl={navigateToCoreUrl}
                    />
                </Route>
                <Route path={routes.auth.redirect} exact>
                    <AuthenticationRedirectView
                        proceed={proceedToLogout}
                        navigateToCoreUrl={navigateToCoreUrl}
                        navigateToCachedRoute={navigateToCachedRoute}
                    />
                </Route>
                <Redirect to={routes.auth.redirect}/>
            </Switch>
        </>
    );
}